import React from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames/bind';

import { AdminSingleDocument } from '@SERVICES';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useDocumentIntroductionTextModal from '@HOOKS/store/modals/useDocumentIntroductionTextModal';
import useNewChapterModal from '@HOOKS/store/modals/useNewChapterModal';

import ChaptersStickyHeader from '@PAGES/admin/document/components/ChaptersStickyHeader';
import DocumentName from '@PAGES/admin/document/components/DocumentName';
import DocumentIntroductionEmptyState from '@PAGES/admin/document/components/DocumentIntroductionEmptyState';
import DocumentPreviewPage from '@COMPONENTS/SHARED/DocumentPreviewPage';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import Chapter from '@PAGES/admin/document/components/Chapter';

import styles from './Content.module.scss';

const cx: CX = classnames.bind(styles);

function Content(props: Props) {
    const { documentData, top } = props;

    // State for managing text expansion
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [showExpandButton, setShowExpandButton] = React.useState(false);
    const [lastUpdateTime, setLastUpdateTime] = React.useState(Date.now());

    const {
        actions: documentIntroductionTextModalActions,
    } = useDocumentIntroductionTextModal(() => true);

    const {
        actions: newChapterModalActions,
    } = useNewChapterModal(() => true);

    const { canEditDocument } = useDocumentPermissions({
        documentOwnerId: documentData.data.owner.id,
        permissions: documentData.meta.permissions,
    });

    // Use real introduction text data from the API
    const introductionTextData = documentData.data.introduction_text;
    const hasIntroductionText = introductionTextData && introductionTextData.body && introductionTextData.body.trim().length > 0;
    const introductionText = introductionTextData?.body || '';

    // Track when introduction text changes to force iframe update
    React.useEffect(() => {
        if (introductionText) {
            setLastUpdateTime(Date.now());
        }
    }, [introductionText]);

    // Check if text is long enough to need truncation
    React.useEffect(() => {
        if (hasIntroductionText) {
            // Multiple checks to ensure we catch long content
            const textLength = introductionText.replace(/<[^>]*>/g, '').trim().length;
            const wordCount = introductionText.replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
            const estimatedLines = Math.ceil(textLength / 70); // More conservative estimate: 70 chars per line
            
            // Multiple conditions for showing expand button - more aggressive detection
            const shouldShowExpand = (
                estimatedLines > 2 // More than 2 lines (reduced from 3)
                || textLength > 140 // More than 140 characters (reduced from 240)
                || wordCount > 25 // More than 25 words
                || (introductionText.includes('<p>') && introductionText.split('<p>').length > 2) // Multiple paragraphs
            );
            
            // Enhanced debug logging
            console.log('🔍 Introduction text analysis:', {
                textLength,
                wordCount,
                estimatedLines,
                shouldShowExpand,
                hasIntroductionText,
                rawTextSample: `${introductionText.slice(0, 200)}...`,
                textPreview: introductionText.replace(/<[^>]*>/g, '').slice(0, 100),
            });
            
            setShowExpandButton(shouldShowExpand);
        } else {
            setShowExpandButton(false);
        }
    }, [introductionText, hasIntroductionText]);

    const handleOpenIntroductionModal = () => {
        documentIntroductionTextModalActions.open({
            documentData: documentData.data,
            canEditDocument,
        });
    };

    const handleToggleExpand = () => {
        const newExpandedState = !isExpanded;
        console.log('🔄 Toggling expand state:', { 
            from: isExpanded, 
            to: newExpandedState,
            showExpandButton 
        });
        setIsExpanded(newExpandedState);
    };

    const renderIntroductionSection = () => {
        if (!hasIntroductionText) {
            return (
                <div className="container max-constraint center">
                    <DocumentIntroductionEmptyState
                        onAddIntroduction={handleOpenIntroductionModal}
                        canEdit={canEditDocument}
                    />
                </div>
            );
        }

        return (
            <div className="container max-constraint center">
                <div className={cx('document-introduction-wrapper')}>
                    <div className={cx('document-introduction-header')}>
                        <div className={cx('title-section')}>
                            <h2 className={cx('introduction-title')}>Introduction</h2>
                            <p className={cx('introduction-description')}>
                                This content will appear above the table of contents in the PDF document
                            </p>
                        </div>
                        <div className={cx('header-actions')}>
                            {showExpandButton && (
                                <button
                                    type="button"
                                    className={cx('expand-button')}
                                    onClick={handleToggleExpand}
                                    aria-label={isExpanded ? 'Show less' : 'Show more'}
                                >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                            {canEditDocument && (
                                <BasicButton
                                    title="Edit"
                                    variant="neutral"
                                    size="small"
                                    onClick={handleOpenIntroductionModal}
                                />
                            )}
                        </div>
                    </div>
                    <div className={cx('document-introduction-content', {
                        'is-collapsed': !isExpanded && showExpandButton,
                        'is-expanded': isExpanded,
                    })}
                    >
                        <div className={cx('content-wrapper')}>
                            <DocumentPreviewPage
                                body={introductionText}
                                isLandscape={false}
                                key={`preview-${lastUpdateTime}`}
                            />
                        </div>
                        {!isExpanded && showExpandButton && (
                            <div className={cx('fade-overlay')} />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={cx('content')}>
            <DocumentName
                documentData={documentData}
            />

            {/* Document Introduction Text - appears above table of contents */}
            {renderIntroductionSection()}

            <ChaptersStickyHeader
                top={top}
                documentData={documentData}
            />

            <div className="container max-constraint center">
                <div className={cx('grid')}>
                    <div className={cx('grid-item')}>
                        <div className={cx('add-chapter-card', { disabled: !canEditDocument })}>
                            <button
                                type="button"
                                className={cx('add-chapter-btn')}
                                onClick={() => {
                                    if (canEditDocument) {
                                        newChapterModalActions.open({
                                            documentId: documentData.data.id,
                                        });
                                    }
                                }}
                                disabled={!canEditDocument}
                            >
                                <div className={cx('btn-content')}>
                                    <div className={cx('icon-wrapper')}>
                                        <svg 
                                            className={cx('plus-icon')} 
                                            viewBox="0 0 24 24" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path 
                                                d="M12 5V19M5 12H19" 
                                                stroke="currentColor" 
                                                strokeWidth="2.5" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        <div className={cx('icon-glow')} />
                                    </div>
                                    <div className={cx('text-content')}>
                                        <h3 className={cx('title')}>Create New Chapter</h3>
                                        <p className={cx('description')}>
                                            Add a new chapter to organize your content
                                        </p>
                                    </div>
                                </div>
                                <div className={cx('card-shine')} />
                            </button>
                        </div>
                    </div>

                    {
                        documentData!.data.chapters.map((chapter, i) => {
                            const chapterKey = chapter.id;

                            return (
                                <div
                                    key={chapterKey}
                                    className={cx('grid-item')}
                                >
                                    <Chapter
                                        documentId={documentData.data.id}
                                        documentOwnerId={documentData.data.owner.id}
                                        permissions={documentData.meta.permissions}
                                        chapter={chapter}
                                        index={i}
                                    />
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        </div>
    );
}

Content.propTypes = {
    documentData: PropTypes.shape({
        data: PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            introduction_text: PropTypes.shape({
                body: PropTypes.string.isRequired,
            }).isRequired,
            owner: PropTypes.shape({
                id: PropTypes.number.isRequired,
            }).isRequired,
        }).isRequired,
        meta: PropTypes.shape({
            permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
        }).isRequired,
    }).isRequired,
    top: PropTypes.number.isRequired,
};

type Props = {
    documentData: AdminSingleDocument;
    top: number;
};

export default Content;
